import { Controller } from "@hotwired/stimulus"

// Client half of the dual validation on the Video Details step.
//
// Mirrors the model's required fields and code formats so a mistake is caught
// before the round trip; the model remains the authority and re-renders the
// same summary server-side if anything slips through.
export default class extends Controller {
  static targets = ["form", "submitButton", "submitButtonSpinner", "submitButtonText", "videoType", "subtypeField", "uploadNotice"]

  static LIVE_PERFORMANCE = "live_performance"

  static REQUIRED_FIELDS = [
    { name: "music_video[title]", labelKey: "song_title" },
    { name: "music_video[isrc]", labelKey: "isrc" },
    { name: "music_video[video_type]", labelKey: "video_type" },
    { name: "music_video[live_date]", labelKey: "live_date" },
    { name: "music_video[genre]", labelKey: "genre" },
    { name: "music_video[audio_language]", labelKey: "audio_language" },
    { name: "music_video[p_line_copyright]", labelKey: "p_line" },
    { name: "music_video[c_line_copyright]", labelKey: "c_line" },
    { name: "music_video[label_name]", labelKey: "label_name" },
    { name: "music_video[original_release_date]", labelKey: "original_release_date" }
  ]

  connect() {
    this.formTarget.addEventListener("submit", this.validateForm.bind(this))
    this.toggleSubtype()
    this.connectUploadWatch()
  }

  disconnect() {
    this.disconnectUploadWatch()
  }

  // The subtype belongs to a live performance and nothing else. Hiding it also
  // clears it, so switching type never leaves a stale value behind to save.
  toggleSubtype() {
    if (!this.hasVideoTypeTarget || !this.hasSubtypeFieldTarget) return

    const live = this.videoTypeTarget.value === this.constructor.LIVE_PERFORMANCE
    this.subtypeFieldTarget.classList.toggle("hidden", !live)

    if (!live) {
      const select = this.subtypeFieldTarget.querySelector("select")
      if (select) select.value = ""
    }
  }

  get subtypeRequired() {
    return this.hasVideoTypeTarget && this.videoTypeTarget.value === this.constructor.LIVE_PERFORMANCE
  }

  // Save and Continue posts the form, which would navigate away from an upload
  // still in flight. The upload controller announces itself on the document, so
  // the two need no knowledge of each other beyond these two events.
  connectUploadWatch() {
    this.uploadsInFlight = 0
    this.onUploadStarted = () => this.setUploadsInFlight(this.uploadsInFlight + 1)
    this.onUploadFinished = () => this.setUploadsInFlight(this.uploadsInFlight - 1)

    document.addEventListener("music-video:upload-started", this.onUploadStarted)
    document.addEventListener("music-video:upload-finished", this.onUploadFinished)
  }

  disconnectUploadWatch() {
    document.removeEventListener("music-video:upload-started", this.onUploadStarted)
    document.removeEventListener("music-video:upload-finished", this.onUploadFinished)
  }

  setUploadsInFlight(count) {
    this.uploadsInFlight = Math.max(count, 0)
    const busy = this.uploadsInFlight > 0

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = busy
      this.submitButtonTarget.classList.toggle("opacity-75", busy)
      this.submitButtonTarget.classList.toggle("cursor-not-allowed", busy)
    }

    if (this.hasUploadNoticeTarget) {
      this.uploadNoticeTarget.textContent = busy ? this.t("music_videos.edit.upload_in_progress") : ""
      this.uploadNoticeTarget.classList.toggle("hidden", !busy)
    }
  }

  get uploading() {
    return (this.uploadsInFlight || 0) > 0
  }

  validateForm(event) {
    // Saving mid-upload would navigate away and abort the transfer, so it is
    // refused with the reason rather than silently ignored.
    if (this.uploading) {
      event.preventDefault()
      this.showValidationErrors([ this.t("music_videos.edit.upload_in_progress") ])
      return false
    }

    // Clear the last attempt's highlights before checking, so each error below
    // can mark its own field without being wiped when the summary is drawn.
    this.removeExistingErrors()
    const errors = this.collectValidationErrors()

    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(errors)
      return false
    }

    this.setSubmitButtonLoading(true)
    return true
  }

  collectValidationErrors() {
    const errors = []

    this.constructor.REQUIRED_FIELDS.forEach(field => {
      const input = this.formTarget.querySelector(`[name="${field.name}"]`)
      if (input && this.isFieldEmpty(input)) {
        errors.push(this.t(`music_videos.edit.validation.is_required`, {
          field: this.t(`music_videos.edit.validation.fields.${field.labelKey}`)
        }))
        this.markFieldInvalid(input)
      }
    })

    // Only required while the type is a live performance.
    if (this.subtypeRequired) {
      const subtype = this.formTarget.querySelector('[name="music_video[video_subtype]"]')
      if (subtype && this.isFieldEmpty(subtype)) {
        errors.push(this.t("music_videos.edit.validation.is_required", {
          field: this.t("music_videos.edit.validation.fields.video_subtype")
        }))
        this.markFieldInvalid(subtype)
      }
    }

    // Format checks mirror the model, so a wrong code is caught here rather
    // than after a round trip.
    const upc = this.formTarget.querySelector('[name="music_video[upc]"]')
    if (upc && upc.value.trim() && !/^\d{12,13}$/.test(upc.value.trim())) {
      errors.push(this.t("music_videos.edit.validation.upc_format"))
      this.markFieldInvalid(upc)
    }

    // The MP4 is always required; Apple Music additionally wants the MOV, which
    // is why that slot is only on screen when Apple Music is selected. Each
    // missing file is named on its own - "one of them" is not actionable.
    const missingFiles = this.missingVideoFiles
    missingFiles.forEach(slot => errors.push(this.t(`music_videos.edit.validation.${slot}_required`)))
    this.markBlockInvalid('[data-controller~="video-file-upload"]', missingFiles.length > 0)

    // Artwork is required at the same point as the master - both are part of
    // what makes the release deliverable.
    if (!this.thumbnailAttached) {
      errors.push(this.t("music_videos.edit.validation.thumbnail_required"))
      this.markBlockInvalid('[data-controller~="video-thumbnail-upload"]', true)
    } else {
      this.markBlockInvalid('[data-controller~="video-thumbnail-upload"]', false)
    }

    // Credits live in their own card and save themselves, but the video cannot
    // be saved without someone credited as the performer.
    const roles = Array.from(this.formTarget.querySelectorAll('select[name="music_video[credits][][role]"]'))
    if (!roles.some(role => role.value === "primary")) {
      errors.push(this.t("music_videos.edit.validation.primary_artist_required"))
      this.markCreditsInvalid(true)
    } else {
      this.markCreditsInvalid(false)
    }

    const liveDate = this.formTarget.querySelector('[name="music_video[live_date]"]')
    const originalDate = this.formTarget.querySelector('[name="music_video[original_release_date]"]')
    if (liveDate && originalDate && liveDate.value && originalDate.value && liveDate.value < originalDate.value) {
      errors.push(this.t("music_videos.edit.validation.live_date_before_original"))
      this.markFieldInvalid(liveDate)
    }

    return errors
  }

  // Whether either slot holds a master. The upload controller keeps the flag on
  // each slot as files come and go, so this stays true after an upload without
  // a page reload - and false again after a delete.
  // Which of the slots on screen are still empty. A slot that is not rendered
  // is not required - the MOV one appears only for Apple Music.
  get missingVideoFiles() {
    return [ "mp4", "mov" ].filter(slot => {
      const element = this.formTarget.querySelector(`[data-video-file-upload-target="slot"][data-slot="${slot}"]`)
      return element !== null && element.dataset.hasFile !== "true"
    })
  }

  get thumbnailAttached() {
    return this.formTarget.querySelector('[data-controller~="video-thumbnail-upload"][data-has-thumbnail="true"]') !== null
  }

  // Cards with no single input to redden get outlined instead.
  markBlockInvalid(selector, invalid) {
    const card = this.formTarget.querySelector(selector)
    if (!card) return

    card.classList.toggle("ring-2", invalid)
    card.classList.toggle("ring-red-500", invalid)
  }

  markCreditsInvalid(invalid) {
    this.markBlockInvalid('[data-controller~="video-credits"]', invalid)
  }

  isFieldEmpty(input) {
    return !input.value || input.value.trim() === ""
  }

  t(key, options = {}) {
    if (window.I18n && typeof window.I18n.t === "function") {
      return window.I18n.t(key, options)
    }
    return key
  }

  showValidationErrors(errors) {
    this.removeErrorSummaries()

    const container = document.createElement("div")
    container.className = "rounded-md bg-red-50 p-4 mb-6"
    container.id = "client-validation-errors"

    const heading = errors.length === 1
      ? this.t("music_videos.edit.validation.errors_one", { count: 1 })
      : this.t("music_videos.edit.validation.errors_other", { count: errors.length })

    container.innerHTML = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">${heading}</h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${errors.map(error => `<li>${error}</li>`).join("")}
            </ul>
          </div>
        </div>
      </div>
    `

    this.formTarget.insertBefore(container, this.formTarget.firstChild)
    container.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  removeErrorSummaries() {
    const existing = document.getElementById("client-validation-errors")
    if (existing) existing.remove()

    // The server's summary from the previous save is stale once the form is
    // checked again here - leaving it would show two error boxes at once.
    const serverErrors = document.getElementById("music-video-errors")
    if (serverErrors) serverErrors.remove()
  }

  // Summaries plus every field and card highlight.
  removeExistingErrors() {
    this.removeErrorSummaries()

    this.formTarget.querySelectorAll(".border-red-500").forEach(field => {
      field.classList.remove("border-red-500", "focus:border-red-500", "focus:ring-red-500")
      field.classList.add("border-gray-300", "focus:border-gray-900", "focus:ring-gray-900")
    })

    this.formTarget.querySelectorAll(".ts-control.has-error").forEach(control => {
      control.classList.remove("has-error")
    })

    this.formTarget.querySelectorAll(".ring-red-500").forEach(card => {
      card.classList.remove("ring-2", "ring-red-500")
    })
  }

  markFieldInvalid(input) {
    if (input.tomselect) {
      // The native select is hidden - redden the control TomSelect drew.
      input.tomselect.control.classList.add("has-error")
    } else {
      input.classList.remove("border-gray-300", "focus:border-gray-900", "focus:ring-gray-900")
      input.classList.add("border-red-500", "focus:border-red-500", "focus:ring-red-500")
    }
  }

  setSubmitButtonLoading(loading) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = loading
    this.submitButtonTarget.classList.toggle("opacity-75", loading)
    this.submitButtonTarget.classList.toggle("cursor-not-allowed", loading)

    if (this.hasSubmitButtonSpinnerTarget) {
      this.submitButtonSpinnerTarget.classList.toggle("hidden", !loading)
    }

    if (this.hasSubmitButtonTextTarget && loading) {
      this.submitButtonTextTarget.textContent = this.t("music_videos.edit.saving")
    }
  }
}
